import { toPng } from 'html-to-image';
import jsPDF from 'jspdf';

interface GeneratePDFOptions {
  element: HTMLElement;
  selector: string;
  filename?: string;
  margin?: number;
  returnBlob?: boolean;
}

interface BlockImage {
  dataUrl: string;
  width: number;
  height: number;
  heightInMm: number;
}

const renderBlockToImage = async (block: HTMLElement, contentWidth: number): Promise<BlockImage> => {
  const dataUrl = await toPng(block, {
    quality: 1,
    pixelRatio: 2,
    backgroundColor: '#ffffff',
    skipFonts: true
  });

  const img = new Image();
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = dataUrl;
  });

  const heightInMm = (img.height * contentWidth) / img.width;

  return {
    dataUrl,
    width: img.width,
    height: img.height,
    heightInMm
  };
};

export const generatePDFFromElement = async ({
  element,
  selector,
  filename,
  margin = 15,
  returnBlob = false
}: GeneratePDFOptions): Promise<Blob | void> => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const contentWidth = pageWidth - margin * 2;

  const blocks = element.querySelectorAll(`[${selector}]`) as NodeListOf<HTMLElement>;

  if (blocks.length === 0) {
    const blockImage = await renderBlockToImage(element, contentWidth);
    pdf.addImage(blockImage.dataUrl, 'PNG', margin, margin, contentWidth, blockImage.heightInMm);
    if (returnBlob) {
      return pdf.output('blob');
    }
    pdf.save(filename!);
    return;
  }

  let currentY = margin;
  const gap = 5;

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];

    try {
      const blockImage = await renderBlockToImage(block, contentWidth);

      if (currentY + blockImage.heightInMm > pageHeight - margin) {
        pdf.addPage();
        currentY = margin;
      }

      pdf.addImage(blockImage.dataUrl, 'PNG', margin, currentY, contentWidth, blockImage.heightInMm);
      currentY += blockImage.heightInMm + gap;
    } catch (error) {
      console.error('Error rendering block to PDF:', error);
    }
  }

  if (returnBlob) {
    return pdf.output('blob');
  }
  pdf.save(filename!);
};
